import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  SimpleChanges,
  ViewChild,
  ElementRef,
  HostListener,
  NgZone,
  Renderer2
} from '@angular/core';
import { CartService } from '../../../../shared/services/cart.service';
import { CurrencyService } from '../../../../core/services/currency.service';
import {
  getCartItemMaximumQuantity,
  getCartItemMinimumQuantity,
  getCartItemUnitPriceHT,
} from '../../../../shared/cart-pricing.util';
import { BehaviorSubject, Subscription } from 'rxjs';

interface CartSummary {
  productsSubtotal: number;
  downloadsSubtotal: number;
  vat: number;
  grandTotal: number;
}

interface CartView {
  cart: any[];
  quantity: number;
  summary: CartSummary;
}

const EMPTY_CART_VIEW: CartView = {
  cart: [],
  quantity: 0,
  summary: {
    productsSubtotal: 0,
    downloadsSubtotal: 0,
    vat: 0,
    grandTotal: 0,
  },
};

@Component({
  selector: 'app-cart-dropdown',
  standalone: false,
  templateUrl: './cart-dropdown.component.html',
  styleUrl: './cart-dropdown.component.scss',
})
export class CartDropdownComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public isOpen: boolean = false;
  @Input() public isClosing: boolean = false;
  @Output() public close = new EventEmitter<void>();
  dropdownCurrency: string = 'EUR';

  private readonly cartViewSubject = new BehaviorSubject<CartView>(EMPTY_CART_VIEW);
  readonly cartView$ = this.cartViewSubject.asObservable();
  removedItemName = '';
  quantityUpdateError = '';
  private removedItem: { item: any; index: number } | null = null;
  private undoTimeout?: ReturnType<typeof setTimeout>;
  private returnFocusElement: HTMLElement | null = null;
  @ViewChild('closeCartButton') private closeCartButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('cartDrawer') private cartDrawer?: ElementRef<HTMLElement>;
  private cartSubscription: Subscription;
  private currencySubscription?: Subscription;
  private cartNoticeSubscription?: Subscription;
  cartNotice: string | null = null;

  constructor(
    private readonly cartService: CartService,
    private readonly currencyService: CurrencyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly renderer: Renderer2,
  ) {
    this.cartSubscription = this.cartService.cart$.subscribe((cart) => {
      this.ngZone.run(() => {
        this.cartViewSubject.next(this.createCartView(cart));
      });
    });
  }

  ngOnInit(): void {
    this.dropdownCurrency = this.currencyService.currentCurrency;
    this.currencySubscription = this.currencyService.currency$.subscribe((currency) => {
      this.dropdownCurrency = currency;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    });
    this.cartNoticeSubscription = this.cartService.cartNotice$.subscribe((notice) => {
      this.cartNotice = notice;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (typeof document !== 'undefined') {
        if (changes['isOpen'].currentValue) {
          this.renderer.addClass(document.body, 'cart-drawer-open');
        } else {
          this.renderer.removeClass(document.body, 'cart-drawer-open');
        }
      }
    }

    if (changes['isOpen']?.currentValue) {
      if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
        this.returnFocusElement = document.activeElement;
      }
      setTimeout(() => this.closeCartButton?.nativeElement.focus());
    } else if (changes['isOpen'] && !changes['isOpen'].currentValue) {
      setTimeout(() => this.returnFocusElement?.focus());
      this.returnFocusElement = null;
    }
  }

  getValue(value: any): any {
    return value;
  }

  getVariantValue(variantValue: any, variantLabel = ''): string {
    let value = '';
    if (typeof variantValue === 'string' || typeof variantValue === 'number') {
      value = String(variantValue);
    } else if (variantValue?.value != null) {
      value = String(variantValue.value);
    } else if (variantValue?.label != null) {
      value = String(variantValue.label);
    } else if (variantValue?.name != null) {
      value = String(variantValue.name);
    } else if (variantValue != null) {
      value = String(variantValue);
    }

    if (variantLabel.trim().replace(/:+$/, '').toLowerCase() === 'sold by') {
      value = value.replace(/^sold by\b\s*:?\s*/i, '');
    }

    return value.trim();
  }

  getVariantLabel(variantKey: any, product: any): string {
    // Convert to string to handle any type
    const key = String(variantKey);
    let result = '';

    // Find the variant definition in the product's variants array
    if (product?.variants && Array.isArray(product.variants)) {
      const variantDef = product.variants.find((v: any) => {
        return String(v?.variant?.id) === key;
      });

      if (variantDef?.variant?.label) {
        result = variantDef.variant.label;
      } else if (variantDef?.variant?.key) {
        const keyLabelMap: { [key: string]: string } = {
          'number': 'Numer',
          'size': 'Rozmiar',
          'color': 'Kolor',
          'material': 'Materiał',
          'weight': 'Masa',
          'dimension': 'Wymiary'
        };
        result = keyLabelMap[variantDef.variant.key] || variantDef.variant.key;
      }
    }

    if (!result) {
      // If no product variants found, try to parse from known variant IDs
      // This is a fallback for common variant types based on ID patterns
      if (key === '60001') {
        result = 'Rozmiar';
      } else if (key === '90002') {
        result = 'Numer';
      } else {
        // Final fallback - check if the key itself is a known label key
        const keyLabelMap: { [key: string]: string } = {
          'number': 'Numer',
          'size': 'Rozmiar',
          'color': 'Kolor',
          'material': 'Materiał',
          'weight': 'Masa',
          'dimension': 'Wymiary'
        };
        result = keyLabelMap[key] || key;
      }
    }

    // Strip any trailing colons and extra spacing
    if (result && typeof result === 'string') {
      result = result.replace(/:+$/, '').trim();
    }

    return result;
  }

  get cart(): any[] {
    return this.cartViewSubject.value.cart;
  }

  get cartSummary(): CartSummary {
    return this.cartViewSubject.value.summary;
  }

  getCartTotal(): number {
    return this.cartSummary.grandTotal;
  }

  getTotalHT(item: any) {
    return this.getUnitPriceHT(item) * (Number(item?.quantity) || 0);
  }

  getUnitPriceHT(item: any): number {
    return getCartItemUnitPriceHT(item);
  }

  getFormattedUnitPrice(item: any): string {
    const currency = this.currencyService.currentCurrency;
    let price = this.getUnitPriceHT(item);
    if (currency === 'PLN') {
      const rate = this.currencyService.currentRate;
      if (!Number.isFinite(rate) || rate <= 0) {
        return 'Nie można przeliczyć waluty. Spróbuj ponownie później.';
      }
      price *= rate;
    }

    const quantity = Math.max(1, Number(item?.quantity) || 1);
    const fractionDigits = Math.min(8, Math.max(4, Math.ceil(Math.log10(quantity)) + 3));
    const formattedPrice = price.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: fractionDigits,
    });
    return `${formattedPrice} ${currency === 'PLN' ? 'zł' : '€'}`;
  }

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      this.renderer.removeClass(document.body, 'cart-drawer-open');
    }
    this.cartSubscription.unsubscribe();
    this.currencySubscription?.unsubscribe();
    this.cartNoticeSubscription?.unsubscribe();
    if (this.undoTimeout) clearTimeout(this.undoTimeout);
    this.cartViewSubject.complete();
  }

  protected onRemoveFromCart(item: any) {
    if (this.undoTimeout) clearTimeout(this.undoTimeout);
    const index = this.cart.findIndex(cartItem => String(cartItem?.id) === String(item?.id));
    if (index === -1) return;

    this.removedItem = { item, index };
    this.removedItemName = item?.product?.label || 'Produkt';
    this.cartService.removeFromCart(item?.id);
    this.undoTimeout = setTimeout(() => this.clearUndo(), 5000);
  }

  undoRemove(): void {
    if (!this.removedItem) return;
    this.cartService.restoreCartItem(this.removedItem.item, this.removedItem.index);
    this.clearUndo();
  }

  private clearUndo(): void {
    if (this.undoTimeout) clearTimeout(this.undoTimeout);
    this.undoTimeout = undefined;
    this.removedItem = null;
    this.removedItemName = '';
  }

  getMinimumQuantity(item: any): number {
    return getCartItemMinimumQuantity(item);
  }

  getMaximumQuantity(item: any): number {
    return getCartItemMaximumQuantity(item);
  }

  canDecreaseQuantity(item: any): boolean {
    return Number(item?.quantity) > this.getMinimumQuantity(item);
  }

  canIncreaseQuantity(item: any): boolean {
    const minimum = this.getMinimumQuantity(item);
    const current = Number(item?.quantity);
    const next = current < minimum
      ? minimum
      : current % minimum === 0
        ? current + minimum
        : Math.ceil(current / minimum) * minimum;
    return Number.isInteger(current) && next <= this.getMaximumQuantity(item);
  }

  getQuantityLimitMessage(item: any): string {
    const maximum = this.getMaximumQuantity(item);
    const minimum = this.getMinimumQuantity(item);
    return maximum < minimum
      ? `Dostępnych jest tylko ${maximum} szt. Minimalna ilość zamówienia to ${minimum} szt.`
      : `Maksymalna dostępna ilość: ${maximum} szt.`;
  }

  changeQuantity(
    item: any,
    direction: -1 | 1,
    itemIndex = this.cart.findIndex(
      cartItem => cartItem === item || String(cartItem?.id) === String(item?.id)
    )
  ): void {
    const minimum = this.getMinimumQuantity(item);
    const current = Number(item?.quantity);
    if (!Number.isFinite(current) || current <= 0 || !item?.id) return;

    const next = direction === 1
      ? current < minimum
        ? minimum
        : current % minimum === 0
          ? current + minimum
          : Math.ceil(current / minimum) * minimum
      : current % minimum === 0
        ? Math.max(minimum, current - minimum)
        : Math.max(minimum, Math.floor(current / minimum) * minimum);
    if (next === current) return;
    if (direction === 1 && next > this.getMaximumQuantity(item)) {
      this.quantityUpdateError = this.getQuantityLimitMessage(item);
      this.cdr.detectChanges();
      return;
    }

    if (!this.cartService.updateCartAtIndex(itemIndex, next)) {
      this.quantityUpdateError = `Nie udało się zmienić ilości produktu „${this.getProductName(item)}”. Spróbuj ponownie.`;
      this.cdr.detectChanges();
      return;
    }

    this.quantityUpdateError = '';
    this.cdr.markForCheck();
  }

  getProductName(item: any): string {
    return item?.product?.label || item?.product?.name || 'Produkt';
  }

  onProductImageError(event: Event): void {
    const image = event.target;
    if (!(image instanceof HTMLImageElement) || image.dataset['fallbackApplied']) return;
    image.dataset['fallbackApplied'] = 'true';
    image.src = '/images/placeholder-product.svg';
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isOpen || this.isClosing) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const drawer = this.cartDrawer?.nativeElement;
    if (!drawer) return;
    const focusable = Array.from(drawer.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(element => element.offsetParent !== null);
    if (!focusable.length) {
      event.preventDefault();
      drawer.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }
  private createCartView(cart: any[]): CartView {
    const view = cart.reduce<{ quantity: number; summary: CartSummary }>((total, item) => {
      const quantity = Number(item?.quantity) || 0;
      const priceHT = this.getUnitPriceHT(item);
      const tvaRate = Number(item?.product?.tvaRate) || 0;
      const downloadsPrice = (item?.selectedDownloads || []).reduce(
        (sum: number, download: any) => sum + (Number(download?.price) || 0),
        0
      );
      const productTotal = priceHT * quantity;
      const vat = (productTotal + downloadsPrice) * (tvaRate / 100);

      total.quantity += quantity;
      total.summary.productsSubtotal += productTotal;
      total.summary.downloadsSubtotal += downloadsPrice;
      total.summary.vat += vat;
      total.summary.grandTotal += productTotal + downloadsPrice + vat;
      return total;
    }, {
      quantity: 0,
      summary: {
        productsSubtotal: 0,
        downloadsSubtotal: 0,
        vat: 0,
        grandTotal: 0,
      },
    });

    return { cart, ...view };
  }

  getTotal() {
    const view = this.cartViewSubject.value;
    return {
      totalHt: view.summary.productsSubtotal,
      products: view.cart.length,
      quantity: view.quantity,
    };
  }

  calculateProductsOnlyPrice(): number {
    return this.cartSummary.productsSubtotal;
  }

  calculateDownloadsPrice(): number {
    return this.cartSummary.downloadsSubtotal;
  }

  calculateTotalPrice(): number {
    // This now combines products and downloads for backward compatibility
    return this.calculateProductsOnlyPrice() + this.calculateDownloadsPrice();
  }

  calculateVATAmount(): number {
    return this.cartSummary.vat;
  }

  calculateGrandTotal(): number {
    return this.cartSummary.grandTotal;
  }

  getFormattedGrandTotal(): string {
    const { productsSubtotal, downloadsSubtotal, vat } = this.cartSummary;
    const amounts = [productsSubtotal, downloadsSubtotal, vat].map((amount) =>
      this.currencyService.currentCurrency === 'PLN'
        ? this.currencyService.convertFromEur(amount)
        : Number(amount.toFixed(2)),
    );
    if (amounts.some((amount) => !Number.isFinite(amount))) {
      return 'Nie można przeliczyć waluty. Spróbuj ponownie później.';
    }
    const total = amounts.reduce((sum, amount) => sum + amount, 0);
    const symbol = this.currencyService.currentCurrency === 'PLN' ? 'zł' : '€';
    return `${total.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${symbol}`;
  }

  getVATRate(): number {
    if (this.cart.length > 0) {
      return this.cart[0]?.product?.tvaRate || 0;
    }
    return 0;
  }

  /**
   * Get product image URL with multiple fallback options
   */
  getProductImageUrl(item: any): string {
    const product = item?.product;
    if (!product) {
      return '/images/placeholder-product.svg';
    }

    // Try different possible image sources
    // 1. Direct documents array
    if (product.documents && product.documents.length > 0 && product.documents[0]?.url) {
      return product.documents[0].url;
    }

    // 2. Colors -> documents array
    if (product.colors && product.colors.length > 0 && product.colors[0]?.documents && product.colors[0].documents.length > 0) {
      return product.colors[0].documents[0].url;
    }

    // 3. Direct imageUrl field
    if (product.imageUrl) {
      // Handle base64 data URLs
      if (product.imageUrl.startsWith('data:')) {
        return product.imageUrl;
      }
      // Handle base64 without prefix
      if (product.imageUrl.length > 100 && !product.imageUrl.startsWith('http')) {
        return `data:image/png;base64,${product.imageUrl}`;
      }
      return product.imageUrl;
    }

    // 4. Direct image field
    if (product.image) {
      return product.image;
    }

    // 5. Selected color documents (for variants)
    if (item.selectedColor?.documents && item.selectedColor.documents.length > 0) {
      return item.selectedColor.documents[0].url;
    }

    // Fallback to placeholder
    return '/images/placeholder-product.svg';
  }

  onClose(): void {
    this.close.emit();
  }

  getColorHex(colorName: string): string {
    if (!colorName) return '#cccccc';

    // Common color mappings
    const colorMap: { [key: string]: string } = {
      'black': '#000000',
      'white': '#ffffff',
      'red': '#ef4444',
      'blue': '#3b82f6',
      'green': '#22c55e',
      'yellow': '#eab308',
      'orange': '#f97316',
      'purple': '#a855f7',
      'pink': '#ec4899',
      'gray': '#6b7280',
      'grey': '#6b7280',
      'brown': '#92400e',
      'navy': '#1e40af',
      'lime': '#84cc16',
      'cyan': '#06b6d4',
      'indigo': '#6366f1',
      'violet': '#8b5cf6',
      'rose': '#f43f5e',
      'emerald': '#10b981',
      'teal': '#14b8a6',
      'sky': '#0ea5e9',
      'amber': '#f59e0b',
      'neutral': '#737373',
      'slate': '#64748b',
      'zinc': '#71717a',
      'stone': '#78716c',
    };

    // Try to find exact match first
    const lowerColorName = colorName.toLowerCase();
    if (colorMap[lowerColorName]) {
      return colorMap[lowerColorName];
    }

    // Try to find partial match
    for (const [key, value] of Object.entries(colorMap)) {
      if (lowerColorName.includes(key) || key.includes(lowerColorName)) {
        return value;
      }
    }

    // If no match found, generate a color based on the string
    let hash = 0;
    for (let i = 0; i < colorName.length; i++) {
      hash = colorName.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 60%, 50%)`;
  }
}
